import { CreatorProfileCardType, CreatorProfileImageSize, GetCreatorProfileResponse } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { getImageShape, isDefined } from '../helpers/utils';

import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { detectMobileViewport } from '../helpers/device';
import { ifDefined } from 'lit/directives/if-defined';

interface SocialButton {
    url: string;
    type: 'facebook' | 'instagram' | 'pinterest' | 'other' | 'twitter' | 'linkedin' | 'tiktok';
    cta?: string;
}

@customElement('creator-profile-card')
export class CreatorProfileCard extends LitElement {
    @property({ type: Object })
    creatorProfile?: GetCreatorProfileResponse;

    @property({ type: Boolean })
    useVerticalLayout: boolean = false;

    @property({ type: Boolean })
    showDescription: boolean = true;

    @property({ type: String })
    imageSize: CreatorProfileImageSize = CreatorProfileImageSize.LARGE;

    @property({ type: Boolean })
    showSocialCtas: boolean = true;

    @property({ type: Boolean })
    linkToCreatorProfile: boolean = false;

    @property({ type: String })
    type: CreatorProfileCardType = CreatorProfileCardType.CREATORPROFILEPAGE;

    @state()
    imageDimension: string = 'square';

    @state()
    socials: SocialButton[] = [];

    @state()
    isFollowing: boolean = false;

    get headingName() {
        return isDefined(this.creatorProfile?.name) ? this.creatorProfile?.name : `@${this.creatorProfile?.username ?? ''}`;
    }

    get imageUrl() {
        if (isDefined(this.creatorProfile) && isDefined(this.creatorProfile.image)) {
            return this.creatorProfile.image.startsWith('/')
                ? this.creatorProfile.image
                : `/${this.creatorProfile.image}`;
        }
        if(isDefined(this.creatorProfile) && !isDefined(this.creatorProfile.image)) {
            return '/images/creatorimgdefault.png';
        }

        return '';
    }

    private readonly bindAccountState = new StateController(this, accountInfo);

   async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('creatorProfile')) {
            if (isDefined(this.creatorProfile)) {
                if (this.showSocialCtas) {
                    this.buildSocialsMap();
                }

                this.isFollowing = accountInfo.followedProfiles.some(profile => profile.$oid === this.creatorProfile?._id.$oid) ?? false;
                this.requestUpdate();
            }
        }
    }

    private getImageShape() {
        const imageDimension = getImageShape(this.imageUrl);
        this.imageDimension = imageDimension;
        return this.imageDimension;
    }

    private buildSocialsMap() {
        if (isDefined(this.creatorProfile)) {
            if (this.creatorProfile.custom_urls.length > 0) {
                this.creatorProfile.custom_urls.forEach(customUrl => {
                    this.socials.push({type: 'other', url: customUrl.url, cta: customUrl.cta});
                });
            }
            if (this.creatorProfile.tiktok_url) {
                this.socials.push({url: this.creatorProfile.tiktok_url, type: 'tiktok'});
            }
            if (this.creatorProfile.instagram_url) {
                this.socials.push({url: this.creatorProfile.instagram_url, type: 'instagram'});
            }
            if (this.creatorProfile.facebook_url) {
                this.socials.push({url: this.creatorProfile.facebook_url, type: 'facebook'});
            }
            if (this.creatorProfile.pinterest_url) {
                this.socials.push({url: this.creatorProfile.pinterest_url, type: 'pinterest'});
            }
            if (this.creatorProfile.linkedin_url) {
                this.socials.push({url: this.creatorProfile.linkedin_url, type: 'linkedin'});
            }
            if (this.creatorProfile.twitter_url) {
                this.socials.push({url: this.creatorProfile.twitter_url, type: 'twitter'});
            }
        }
    }

    private goToUrl(url: string) {
        window.open(url);
    }

    private async handleFollow() {
        if (isDefined(this.creatorProfile)) {
            if (this.isFollowing) {
                await accountInfo.unfollowProfile(this.creatorProfile._id.$oid);
                this.isFollowing = false;
            } else {
                await accountInfo.followProfile(this.creatorProfile?._id.$oid);
                this.isFollowing = true;
            }
        }
    }


    render() {
        getImageShape(this.imageUrl);
        return html`
            <div class="creator-profile__header ${this.useVerticalLayout ? 'creator-profile__header--vertical' : 'creator-profile__header--horizontal'}">
                <div class="circular--${this.imageDimension} creator-profile__avatar--${this.imageSize}">
                    <img .src="${this.imageUrl}" />
                </div>
                <span class="creator-profile__info ${!this.useVerticalLayout && !this.showDescription ? 'creator-profile__info--horizontal' : ''}">
                    <span class="${!this.useVerticalLayout && !this.showDescription ? 'creator-profile__title-wrapper' : ''}">
                        ${this.linkToCreatorProfile && isDefined(this.creatorProfile)?
                            // eslint-disable-next-line lit/no-invalid-html
                            html`by: <a href="#creator-profile?profile_id=${this.creatorProfile._id.$oid}" class="creator-profile__title creator-profile__title--link">${this.headingName}</a>`
                            : html`
                            <h1 class="creator-profile__title  creator-profile__title--${this.imageSize}">
                                ${this.headingName}
                            </h1>`
                        }
                        <div class="creator-profile__subtitle">${this.creatorProfile?.tagline}</div>
                        ${this.showDescription && this.type === CreatorProfileCardType.CREATORPROFILEPAGE ?
                            html`<span>${this.creatorProfile?.description}</span>`
                            : ''
                        }
                        ${this.type === CreatorProfileCardType.MAPINFODRAWER && detectMobileViewport() ?
                            html`
                                <a class="other-maps-link" href="${ifDefined(this.creatorProfile?.link_set.public_link)}" target="_blank">
                                    Other Maps by @${this.creatorProfile?.username} >
                                </a>
                            ` : ''}
                    </span>


                    ${this.type === CreatorProfileCardType.MAPINFODRAWER ? '' : html`
                        <div class="creator-profile__ctas ${this.useVerticalLayout ? 'creator-profile__ctas--vertical' : 'creator-profile__ctas--horizontal'} ${!this.useVerticalLayout && !this.showDescription ? 'creator-profile__horizontal--no-description' : ''}">
                            <mwc-button class="creator-profile__cta ${this.isFollowing ? 'creator-profile__cta--unfollow' : ''}" ?raised=${!this.isFollowing} ?outlined=${this.isFollowing} @click=${this.handleFollow}>
                                ${this.isFollowing ? 'Following' : 'Follow'}
                            </mwc-button>

                            ${this.showSocialCtas ?
                                this.socials.map(social => {
                                    return html`<mwc-button
                                                    class="creator-profile__cta"
                                                    outlined
                                                    @click=${()=> this.goToUrl(social.url)}>
                                                    ${social.type === 'other' ? social.cta : html`<img class="creator-profile__social-icon" src="/images/socials/${social.type}.png" alt="${social.type} icon" />`}
                                                </mwc-button>`;
                                })
                                : ''
                            }
                        </div>`
                    }
                </span>

            </div>
            ${this.type === CreatorProfileCardType.MAPINFODRAWER && this.showDescription ? html`
                <p class="creator-profile__description-section">
                    ${this.creatorProfile?.description}
                </p>` : ''
            }
        `;
    }

    static get styles() {
        return css`
            .creator-profile__title-wrapper {
                align-self: center;
                flex-basis: 100%;
            }

            .creator-profile__horizontal--no-description {
                margin-top: 0 !important;
                align-self: center;
                margin-left: 10px;
            }

            .creator-profile__title {
                margin: 0px 0;
            }

            .creator-profile__description-section {
                text-align: justify;
                color: var(--theme-input-color);
            }

            .creator-profile__title--link {
                font-weight: bold;
                color: var(--main-invalid-color);
                font-size: 16px;
            }

            .creator-profile__title--medium, .creator-profile__title--small {
                font-size: 1rem;
                font-weight: bold;
            }

            .creator-profile__info--horizontal {
                display: flex;
            }

            .creator-profile__subtitle {
                font-size: 0.7rem;
                margin-bottom: 0.5rem;
            }

            .creator-profile__cta--unfollow {
                background-color: var(--theme-lightest-color);
                border-radius: 6px;
            }

            .creator-profile__info {
                margin-left: 10px;
                flex: 1;
            }

            .creator-profile__social-icon {
                height: 24px;
            }

            .other-maps-link {
                text-decoration: none;
                font-weight: 600;
                color: var(--main-invalid-color);
            }

            .creator-profile__cta {
                margin-right: 10px;
                margin-bottom: 6px;
                --mdc-button-outline-color: var(--main-primary-color);
            }

            .creator-profile__ctas {
                --mdc-typography-button-text-transform: case;
                --mdc-typography-button-letter-spacing: 0px;
            }

            .creator-profile__avatar {
                border-radius: 50%;
                box-shadow: rgb(31 27 45 / 8%) 0px 0.125rem 0.125rem -0.125rem, rgb(31 27 45 / 8%) 0px 0.25rem 0.75rem;
                object-fit: cover;
                object-position: 50% 25%;
            }


            .creator-profile__avatar--small {
                height: 3rem;
                width: 3rem;
            }

            .creator-profile__avatar--medium {
                height: 4rem;
                width: 4rem;
            }

            .creator-profile__avatar--large {
                height: 180px;
                width: 180px;
            }

            .creator-profile__header--horizontal {
                display: flex;
                align-items: center;
                gap: 10px;
            }

            .creator-profile__header--vertical {
                display: block;
                text-align: left;
                padding: 0 10px;
                font-size: 10px;
            }

            .creator-profile__ctas--horizontal {
                display: flex;
                margin-top: 6px;
            }

            .creator-profile__ctas--vertical {
                margin-top: 20px;
            }

            .circular--landscape {
                display: inline-block;
                position: relative;
                overflow: hidden;
                border-radius: 50%;
                overflow: hidden;
            }

            .circular--landscape img {
                width: auto;
                height: 100%;
                margin-left: -50px;
            }

            .circular--portrait {
                position: relative;
                overflow: hidden;
                border-radius: 50%;
                overflow: hidden;
            }

            .circular--portrait img {
                width: 100%;
                height: auto;
            }

            .circular--square {
                overflow: hidden;
                border-radius: 50%;
            }

            .circular--square img {
                width: 100%;
                height: 100%;
            }
        `;
    }
}
